import { buildCrmPayload } from '../utils/crmPayload';
import { generateRegistrationPdf } from '../utils/pdfExport';
import { apiUrl, getApiBaseUrl } from './apiConfig';

/**
 * Live availability check for a School Code. Fails open (returns available)
 * when there's no API configured or the request errors, so it never blocks a
 * legitimate registration — the submit-time guard is the authoritative check.
 */
export async function checkSchoolCode(code, acsiId = '') {
  const base = apiUrl('/api/registration/check-school-code');
  if (!base) return { available: true, skipped: true };

  const query = new URLSearchParams({ code, acsiId: acsiId || '' });
  try {
    const response = await fetch(`${base}?${query.toString()}`);
    if (!response.ok) return { available: true, skipped: true };
    return await response.json();
  } catch {
    return { available: true, skipped: true };
  }
}

export async function createPaymentIntent({ amountCents, testCount, studentCount, metadata = {} }) {
  const url = apiUrl('/api/payment/create-intent');
  if (!url) {
    return {
      ok: true,
      demo: true,
      paymentIntentId: `demo_${Date.now()}`,
      clientSecret: null,
      message: 'No API base URL — using local demo payment.',
    };
  }

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amountCents, testCount, studentCount, metadata }),
    });
  } catch (networkError) {
    throw new Error(
      `Could not reach the registration API (${url}). Check REACT_APP_API_BASE_URL and that AppSail is deployed. (${networkError.message})`,
    );
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Unable to create payment intent.');
  }

  return { ok: true, ...data };
}

function sanitizeApiError(message, fallback = 'Registration submission failed.') {
  const text = String(message || '').trim();
  if (!text) return fallback;
  if (
    text.startsWith('<') ||
    text.toLowerCase().includes('<html') ||
    text.toLowerCase().includes('page not found')
  ) {
    return (
      'Zoho CRM request failed. Check ZOHO_ACCOUNTS_URL and ZOHO_API_BASE ' +
      'in AppSail env vars (they must match your Zoho data center).'
    );
  }
  return text.length > 400 ? `${text.slice(0, 400)}…` : text;
}

/**
 * Submits registration + PDF to AppSail, which verifies Stripe and syncs Zoho CRM.
 * Without REACT_APP_API_BASE_URL, falls back to local PDF-only demo mode.
 */
export async function submitRegistration(form, paymentMeta = {}) {
  const payload = buildCrmPayload(form, paymentMeta);
  const { blob, filename } = generateRegistrationPdf(form, { payment: paymentMeta });
  const base = getApiBaseUrl();

  if (!base) {
    return {
      ok: true,
      mode: 'local',
      message:
        'Registration prepared locally. Set REACT_APP_API_BASE_URL to sync with the AppSail API / Zoho CRM.',
      payload,
      pdf: { blob, filename },
      payment: {
        method: paymentMeta.method,
        status: paymentMeta.status,
        checkNumber: paymentMeta.checkNumber,
        paymentIntentId: paymentMeta.paymentIntentId,
      },
    };
  }

  const body = new FormData();
  body.append('payload', JSON.stringify(payload));
  body.append('paymentIntentId', paymentMeta.paymentIntentId || '');
  body.append('paymentMethod', paymentMeta.method || 'card');
  body.append('accessCode', paymentMeta.accessCode || '');
  body.append('checkNumber', paymentMeta.checkNumber || '');
  body.append('registrationPdf', blob, filename);

  const response = await fetch(apiUrl('/api/registration/submit'), {
    method: 'POST',
    body,
  }).catch((networkError) => {
    throw new Error(
      `Could not reach the registration API. Check AppSail is up. (${networkError.message})`,
    );
  });

  const rawText = await response.text();
  let result = {};
  try {
    result = rawText ? JSON.parse(rawText) : {};
  } catch {
    result = {};
  }

  if (!response.ok) {
    throw new Error(
      sanitizeApiError(result.error || rawText, 'Registration submission failed.'),
    );
  }

  return {
    ok: true,
    mode: result.mode || 'api',
    message: result.message,
    payload,
    pdf: { blob, filename },
    ...result,
  };
}
